/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('.search');
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.isResolvingSearchQuery = false;
      this.addListeners();
      this.init();
    }

    addListeners() {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (this.isResolvingSearchQuery) {
        this.isResolvingSearchQuery = false;
        return;
      }

      const query = this.getQuery();
      if (!query.length) {
        evt.preventDefault();
        return;
      }

      if (this.shouldResolveQuery(query)) {
        evt.preventDefault();
        this.resolveSearchQuery(query);
      }
    }

    /**
     * Returns the trimmed query.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Indicates if the query is numeric.
     * @param {string} query
     * @returns {boolean}
     */
    shouldResolveQuery(query) {
      const normalizedQuery = (query || '').replace(/\s+/g, '');
      return /^\d{3,}$/.test(normalizedQuery) || /^[A-Za-z]\d{3,}$/.test(normalizedQuery);
    }

    /**
     * Resolves a numeric or PLV query to the exact model code before submitting.
     * @param {string} query
     */
    async resolveSearchQuery(query) {
      try {
        const resolvedCode = await this.lookupSearchQuery(query);
        if (resolvedCode) {
          this.input.value = resolvedCode;
        }
      } finally {
        this.isResolvingSearchQuery = true;
        this.form.requestSubmit();
      }
    }

    /**
     * Looks up the best matching model code from predictive search.
     * @param {string} query
     * @returns {Promise<string>}
     */
    async lookupSearchQuery(query) {
      const normalizedQuery = (query || '').replace(/\s+/g, '');
      const candidates = [normalizedQuery];

      if (/^\d{3,}$/.test(normalizedQuery) || /^[A-Za-z]\d{3,}$/.test(normalizedQuery)) {
        candidates.push(`${normalizedQuery}00`);
      }

      for (const candidate of candidates) {
        const resolvedCode = await this.lookupQueryCandidate(candidate);
        if (resolvedCode) return resolvedCode;
      }

      return '';
    }

    /**
     * Looks up one candidate query in predictive search.
     * @param {string} query
     * @returns {Promise<string>}
     */
    
    async lookupQueryCandidate(query) {
      if (!window.theme || !window.theme.routes || !window.theme.routes.predictiveSearch) {
        return '';
      }

      const url = new URL(window.theme.routes.predictiveSearch, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'variants.sku');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) return '';

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const preferredCode = this.getPreferredCode(query);
      const items = template.content.querySelectorAll('#psearch-products .predictive-search__item[data-upng-model-code]');

      for (const item of items) {
        const modelCode = item.getAttribute('data-upng-model-code');
        if (modelCode && modelCode === preferredCode) {
          return modelCode;
        }
      }

      return '';
    }

    /**
     * Normaliza un candidato de búsqueda al código que queremos resolver.
     * @param {string} query
     * @returns {string}
     */
    getPreferredCode(query) {
      const normalizedQuery = (query || '').replace(/\s+/g, '');
      if ((/^\d{4}$/.test(normalizedQuery) || /^[A-Za-z]\d{3}$/.test(normalizedQuery)) && !normalizedQuery.endsWith('00')) {
        return `${normalizedQuery}00`;
      }

      return normalizedQuery;
    }
  }

  customElements.define('search-form', SearchForm);
}
